/**
 * §9.12 — the Graft. In-run gacha: ichor income, the weighted pull, duplicate folding,
 * the pity floor and the consolation path.
 *
 * GDD: §9.12 (design and tuning), §9.1 (economy map), §9.4 (the level-up channel this
 * sits beside), §9.5 (Lumensurge, which scales the trickle), §6.1 (absolute set-value
 * level tables), §13.1.6 (plain-language deltas). Data: `content/gacha.json`.
 *
 * Three properties are load-bearing and every one of them is tested.
 *
 *  - **Nothing persists.** No meta currency, no collection, no unlock is earned here.
 *    The balance lives in a `WeakMap` keyed by `World` and dies with the run, so spine
 *    §11 needs no amendment and §13.1.10's "same seed + same version ⇒ identical run"
 *    share string keeps working for players whose save files differ.
 *  - **Income draws no RNG.** Ichor is a pure function of what died and of the
 *    Lumensurge tier held at the moment it died. Wiring this module therefore does not
 *    move a single cursor on `loot`, `spawn`, `ai` or `crit`, and every existing replay
 *    keeps reproducing.
 *  - **A pull consumes exactly three `loot` draws, always.** Rarity, then category,
 *    then index — in that order, whatever the pity state, whatever the rarity, whatever
 *    the outcome. Unused draws are still taken. Same seed + same pull ticks ⇒ identical
 *    pull sequence, bit for bit.
 *
 * Money: there is none, and there is no seam for any. Nothing in this file reads a
 * price, a receipt, a store or a network. See AMD-35.
 */

import gachaRaw from '../content/gacha.json';
import { bossById, passiveById, weaponById, MAX_WEAPON_LEVEL, PASSIVES, WEAPONS } from '../core/content';
import { cfg } from '../core/config';
import type { World } from './world';
import { passiveMaxRankOf, titleFromId } from './build';
import {
  MAX_EVOLUTIONS,
  MAX_PASSIVE_SLOTS,
  MAX_WEAPON_SLOTS,
  eligibleEvolutionWeapon,
  evolutionDelta,
  evolutionsTaken,
  grantEvolutionById,
  grantPassiveRank,
  grantWeaponLevel,
  isUnlocked,
  newPassiveDelta,
  newWeaponDelta,
  passiveUpgradeDelta,
  weaponUpgradeDelta,
} from './progression';
import { recomputeStats } from './build';

// --- content table (§16 conventions; see `content/gacha.json`) ---

interface RarityRow {
  id: string;
  display_name: string;
  weight: number;
  new_item_start_level: number;
  duplicate_levels: number;
  grants_evolution: boolean;
  consolation_xp: number;
}

interface GachaTable {
  resource: { id: string; display_name: string };
  income: {
    trash_kill_centi: number;
    surge_bonus_pct_per_tier: number;
    elite_kill: number;
    boss_kill: Record<string, number>;
    default_boss_kill: number;
  };
  cost: { base: number; step: number };
  rarities: RarityRow[];
  pity: { threshold: number; floor_rarity: string };
  pool: {
    category_weights: { duplicate: number; new_item: number };
    reserved_slots: { weapon: number; passive: number };
  };
  consolation: { id: string; display_name: string; heal_hp: number };
}

/**
 * The table. Exported so `src/core/content.ts` can re-export it and, more importantly,
 * so the integrator can fold `gacha.json` into `computeContentHash` — tuning the Graft
 * has to invalidate replays exactly as tuning a weapon does (A.6).
 */
export const GACHA = gachaRaw as unknown as GachaTable;

export const RESOURCE_ID = GACHA.resource.id;
export const RESOURCE_NAME = GACHA.resource.display_name;
export const CONSOLATION_ID = GACHA.consolation.id;

/** Income is accumulated in hundredths so the surge scaling stays exact integer maths. */
const CENTI = 100;

const RARITIES: readonly RarityRow[] = GACHA.rarities;

/** Cumulative rarity weights, built once. Index maps 1:1 onto `RARITIES`. */
const RARITY_CUM = new Float64Array(RARITIES.length);
(() => {
  let acc = 0;
  for (let i = 0; i < RARITIES.length; i++) {
    acc += RARITIES[i].weight;
    RARITY_CUM[i] = acc;
  }
})();

/** Acceptance: exactly 1.0 at data load. */
export function rarityWeightSum(): number {
  return RARITY_CUM.length === 0 ? 0 : RARITY_CUM[RARITY_CUM.length - 1];
}

function rarityIndexOf(id: string): number {
  for (let i = 0; i < RARITIES.length; i++) {
    if (RARITIES[i].id === id) return i;
  }
  return RARITIES.length - 1;
}

const PITY_FLOOR_INDEX = rarityIndexOf(GACHA.pity.floor_rarity);

export const PITY_THRESHOLD = GACHA.pity.threshold;
export const COST_BASE = GACHA.cost.base;
export const COST_STEP = GACHA.cost.step;
export const CATEGORY_DUPLICATE_WEIGHT = GACHA.pool.category_weights.duplicate;
export const RESERVED_WEAPON_SLOTS = GACHA.pool.reserved_slots.weapon;
export const RESERVED_PASSIVE_SLOTS = GACHA.pool.reserved_slots.passive;
export const CONSOLATION_HEAL_HP = GACHA.consolation.heal_hp;

// --- the shape the presentation layer consumes ---

export interface PullResult {
  kind: 'weapon' | 'passive' | 'evolution' | 'duplicate' | 'consolation';
  id: string;
  rarity: string;
  display_name: string;
  /** §13.1.6 plain-language delta. Never empty, on every path including consolation. */
  delta_text: string;
  level_from: number;
  level_to: number;
  /** Consecutive non-floor pulls AFTER this one resolved. */
  pity_progress: number;
  pity_threshold: number;
}

// --- per-world state (dies with the run; nothing is persisted, spine §11) ---

interface GachaState {
  /** Balance in centi-ichor. Spendable ichor is `floor(centi / 100)`. */
  centi: number;
  /** Lifetime centi credited this run, for the run summary and the economy probe. */
  earnedCenti: number;
  pulls: number;
  pity: number;
  /** Edge latch for the `gacha_ready` cue: the pull index the cue last fired for. */
  readyAnnounced: number;
  installed: boolean;
}

const STATES = new WeakMap<World, GachaState>();

function stateOf(w: World): GachaState {
  let st = STATES.get(w);
  if (st === undefined) {
    st = { centi: 0, earnedCenti: 0, pulls: 0, pity: 0, readyAnnounced: -1, installed: false };
    STATES.set(w, st);
  }
  return st;
}

/** Clears the run's ichor, pull count and pity. Call alongside `world.reset(seed)`. */
export function resetGacha(w: World): void {
  const st = stateOf(w);
  st.centi = 0;
  st.earnedCenti = 0;
  st.pulls = 0;
  st.pity = 0;
  st.readyAnnounced = -1;
}

/**
 * Subscribes ichor income to `death`. Idempotent.
 *
 * Install this AFTER `installPickups`, so a kill resolves its §9.2 drops before its
 * ichor. The handler consumes no RNG, so the order does not change any draw — but the
 * event order the presentation layer sees is part of the feel, and drops-then-ichor is
 * the order the HUD is built against.
 */
export function installGacha(w: World): void {
  const st = stateOf(w);
  if (st.installed) return;
  st.installed = true;
  w.bus.on('death', (payload) => {
    creditKill(w, payload.def_id, payload.elite, payload.pos.x, payload.pos.y);
  });
}

// --- §9.12.2 income ---

/** Whole ichor available to spend. */
export function ichorBalance(w: World): number {
  return Math.floor(stateOf(w).centi / CENTI);
}

/** Whole ichor credited over the whole run, spent or not (run summary, §13.1.10). */
export function ichorEarned(w: World): number {
  return Math.floor(stateOf(w).earnedCenti / CENTI);
}

/** Hundredths of an ichor still banked below the next whole unit — the HUD's fill bar. */
export function ichorFractionCenti(w: World): number {
  return stateOf(w).centi % CENTI;
}

export function pullsMade(w: World): number {
  return stateOf(w).pulls;
}

export function pityProgress(w: World): number {
  return stateOf(w).pity;
}

export function pityThreshold(): number {
  return PITY_THRESHOLD;
}

/**
 * §9.12.2 — the trickle. `centi = floor(base × (100 + bonus × (tier − 1)) / 100)`, so a
 * player holding ×10 earns 2.25× what a player sitting at ×1 earns per trash kill. The
 * multiplier is the only place skill enters the income; elites and bosses pay flat.
 */
export function trashCentiAtTier(tier: number): number {
  const t = tier < 1 ? 1 : tier;
  const inc = GACHA.income;
  return Math.floor((inc.trash_kill_centi * (100 + inc.surge_bonus_pct_per_tier * (t - 1))) / 100);
}

export function bossIchorFor(bossId: string): number {
  const v = GACHA.income.boss_kill[bossId];
  return typeof v === 'number' ? v : GACHA.income.default_boss_kill;
}

/**
 * Adds centi-ichor and announces the crossing. `gacha_ichor` fires only when the WHOLE
 * balance moved, so the trickle produces roughly one event per six trash kills instead
 * of one per kill — the bus stays quiet and the HUD still gets a beat to animate.
 */
function credit(w: World, centi: number, source: 'trash' | 'elite' | 'boss', x: number, y: number): void {
  if (centi <= 0) return;
  const st = stateOf(w);
  const before = Math.floor(st.centi / CENTI);
  st.centi += centi;
  st.earnedCenti += centi;
  const after = Math.floor(st.centi / CENTI);
  if (after > before) {
    w.bus.emit('gacha_ichor', { amount: after - before, balance: after, source, pos: { x, y } });
  }
}

/** Income for one death. Pure: reads sim state, draws nothing. */
export function creditKill(w: World, defId: string, elite: boolean, x: number, y: number): void {
  if (!cfg.getBool('gacha.enabled')) return;
  if (bossById.has(defId)) {
    credit(w, bossIchorFor(defId) * CENTI, 'boss', x, y);
    return;
  }
  if (elite) {
    credit(w, GACHA.income.elite_kill * CENTI, 'elite', x, y);
    return;
  }
  credit(w, trashCentiAtTier(w.player.lumensurge_tier), 'trash', x, y);
}

/** Debug / test / integrator hook: hand the player whole ichor with no kill behind it. */
export function grantIchor(w: World, amount: number): void {
  if (amount <= 0) return;
  credit(w, Math.floor(amount) * CENTI, 'boss', w.player.pos.x, w.player.pos.y);
}

/**
 * AMD-52 — the Supply Drop's debit. Whole ichor only; false (and no change) when the
 * balance cannot cover it. Earned-lifetime and pity are untouched: a purchase is not a
 * pull, so it neither advances nor resets §9.12.5's counter.
 */
export function spendIchor(w: World, amount: number): boolean {
  if (amount <= 0) return false;
  const st = stateOf(w);
  const centi = Math.floor(amount) * CENTI;
  if (st.centi < centi) return false;
  st.centi -= centi;
  return true;
}

// --- §9.12.3 cost and gating ---

/** `cost(n) = base + step × n`, n = pulls already made this run. */
export function pullCost(w: World): number {
  return COST_BASE + COST_STEP * stateOf(w).pulls;
}

/**
 * The Graft is available while the run is live, the sim is not behind a modal, and the
 * balance covers the next cost. Blocking on `paused` is deliberate: a level-up draw is
 * open behind that flag and the two decisions must not share a screen (§9.12.7).
 */
export function canPull(w: World): boolean {
  if (!cfg.getBool('gacha.enabled')) return false;
  if (w.outcome !== 'running') return false;
  if (w.paused) return false;
  return ichorBalance(w) >= pullCost(w);
}

// --- candidate pools (§9.12.4) ---

/** Scratch lists. A pull is a player action, not a per-tick path, but reuse is free. */
const dupIds: string[] = [];
const dupIsWeapon: boolean[] = [];
const newIds: string[] = [];
const newIsWeapon: boolean[] = [];

function ownsWeapon(w: World, id: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === id) return true;
  }
  return false;
}

function ownsPassive(w: World, id: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) return true;
  }
  return false;
}

function weaponLevelOf(w: World, id: string): number {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    if (p.weapons[i].def_id === id) return p.weapons[i].level;
  }
  return 0;
}

function passiveRankOf(w: World, id: string): number {
  const p = w.player;
  for (let i = 0; i < p.passive_count; i++) {
    if (p.passives[i].def_id === id) return p.passives[i].rank;
  }
  return 0;
}

/**
 * Owned, unevolved, below cap. The active slot is excluded on purpose: `weapon.gulper-maw`
 * replaces Dash (§9.4.2) and a random graft must never rewrite the defensive verb.
 */
function collectDuplicates(w: World): void {
  dupIds.length = 0;
  dupIsWeapon.length = 0;
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved) continue;
    if (slot.level >= MAX_WEAPON_LEVEL) continue;
    dupIds.push(slot.def_id);
    dupIsWeapon.push(true);
  }
  for (let i = 0; i < p.passive_count; i++) {
    const slot = p.passives[i];
    if (slot.def_id === '') continue;
    if (slot.rank >= passiveMaxRankOf(slot.def_id)) continue;
    dupIds.push(slot.def_id);
    dupIsWeapon.push(false);
  }
}

/**
 * §9.12.4 reserved-slot rule — the Graft may open a weapon or passive slot, but never
 * the LAST one of its family. Whatever else a run gives you, the fourth weapon and the
 * fourth passive are chosen at the level-up screen, by you, on purpose.
 */
function collectNew(w: World): void {
  newIds.length = 0;
  newIsWeapon.length = 0;
  const p = w.player;
  if (p.weapon_count < MAX_WEAPON_SLOTS - RESERVED_WEAPON_SLOTS) {
    for (let i = 0; i < WEAPONS.length; i++) {
      const def = WEAPONS[i];
      if (def.slot !== 'weapon') continue;
      if (!isUnlocked(def.id)) continue;
      if (ownsWeapon(w, def.id)) continue;
      newIds.push(def.id);
      newIsWeapon.push(true);
    }
  }
  if (p.passive_count < MAX_PASSIVE_SLOTS - RESERVED_PASSIVE_SLOTS) {
    for (let i = 0; i < PASSIVES.length; i++) {
      const def = PASSIVES[i];
      if (!isUnlocked(def.id)) continue;
      if (ownsPassive(w, def.id)) continue;
      newIds.push(def.id);
      newIsWeapon.push(false);
    }
  }
}

/** The evolution this pull could grant, or '' — §9.4.4's own eligibility, unchanged. */
function eligibleEvolution(w: World): { weaponId: string; evolutionId: string } | null {
  if (evolutionsTaken(w) >= MAX_EVOLUTIONS) return null;
  const weaponId = eligibleEvolutionWeapon(w);
  if (weaponId === '') return null;
  const def = weaponById.get(weaponId);
  if (def === undefined || def.evolution_id === undefined) return null;
  // §9.12.4: the Graft never touches the active slot, so an active-slot evolution is
  // left for the level-up screen's guaranteed card 1 (§9.4.1).
  if (def.slot !== 'weapon') return null;
  return { weaponId, evolutionId: def.evolution_id as string };
}

// --- §9.12.5 the pull ---

function displayNameOfItem(id: string, isWeapon: boolean): string {
  if (isWeapon) {
    const def = weaponById.get(id);
    return def === undefined ? titleFromId(id) : def.display_name;
  }
  const def = passiveById.get(id);
  return def === undefined ? titleFromId(id) : def.display_name;
}

function consolationResult(w: World, rarity: RarityRow, st: GachaState): PullResult {
  const p = w.player;
  p.xp += rarity.consolation_xp;
  const before = p.hp;
  p.hp = Math.min(p.max_hp, p.hp + CONSOLATION_HEAL_HP);
  const healed = p.hp - before;
  const heal = healed > 0 ? ' and ' + healed + ' HP' : ' (already at full health)';
  return {
    kind: 'consolation',
    id: CONSOLATION_ID,
    rarity: rarity.id,
    display_name: GACHA.consolation.display_name,
    delta_text:
      GACHA.consolation.display_name +
      ' — nothing left to grow, so the ichor vents as light: ' +
      rarity.consolation_xp +
      ' XP' +
      heal +
      '.',
    level_from: 0,
    level_to: 0,
    pity_progress: st.pity,
    pity_threshold: PITY_THRESHOLD,
  };
}

/**
 * §9.12.5 — one Graft.
 *
 * Consumes exactly three draws from `loot`, in this order and unconditionally:
 *
 *   1. rarity — overridden to the pity floor when `pity_progress` has reached the
 *      threshold, but the draw is still taken so the cursor advances identically;
 *   2. category — duplicate versus new item;
 *   3. index — into whichever candidate list step 2 chose.
 *
 * Callers must check {@link canPull} first; a call that cannot afford the cost is a
 * no-op that draws nothing and returns a consolation with a zero pity reading.
 */
export function performPull(w: World): PullResult {
  const st = stateOf(w);
  const cost = pullCost(w);
  if (!canPull(w)) {
    return {
      kind: 'consolation',
      id: CONSOLATION_ID,
      rarity: RARITIES[0].id,
      display_name: GACHA.consolation.display_name,
      delta_text: 'Not enough Ichor — the Graft needs ' + cost + '.',
      level_from: 0,
      level_to: 0,
      pity_progress: st.pity,
      pity_threshold: PITY_THRESHOLD,
    };
  }

  st.centi -= cost * CENTI;
  st.pulls++;

  // --- the three draws, always, in this order ---
  const rRarity = w.rng.loot.next();
  const rCategory = w.rng.loot.next();
  const rIndex = w.rng.loot.next();

  const forced = st.pity >= PITY_THRESHOLD;
  let rIdx = RARITIES.length - 1;
  for (let i = 0; i < RARITIES.length; i++) {
    if (rRarity < RARITY_CUM[i]) {
      rIdx = i;
      break;
    }
  }
  if (forced) rIdx = PITY_FLOOR_INDEX;
  const rarity = RARITIES[rIdx];
  st.pity = rIdx === PITY_FLOOR_INDEX ? 0 : st.pity + 1;

  let result: PullResult;

  const evo = rarity.grants_evolution ? eligibleEvolution(w) : null;
  if (evo !== null) {
    grantEvolutionById(w, evo.evolutionId);
    result = {
      kind: 'evolution',
      id: evo.evolutionId,
      rarity: rarity.id,
      display_name: titleFromId(evo.evolutionId),
      delta_text: evolutionDelta(evo.evolutionId, evo.weaponId),
      level_from: MAX_WEAPON_LEVEL,
      level_to: MAX_WEAPON_LEVEL,
      pity_progress: st.pity,
      pity_threshold: PITY_THRESHOLD,
    };
  } else {
    collectDuplicates(w);
    collectNew(w);
    const useDup = newIds.length === 0 ? true : dupIds.length === 0 ? false : rCategory < CATEGORY_DUPLICATE_WEIGHT;
    const ids = useDup ? dupIds : newIds;
    const isWeaponFlags = useDup ? dupIsWeapon : newIsWeapon;

    if (ids.length === 0) {
      result = consolationResult(w, rarity, st);
    } else {
      let pick = Math.floor(rIndex * ids.length);
      if (pick >= ids.length) pick = ids.length - 1;
      const id = ids[pick];
      const isWeapon = isWeaponFlags[pick];

      if (useDup) {
        const from = isWeapon ? weaponLevelOf(w, id) : passiveRankOf(w, id);
        const cap = isWeapon ? MAX_WEAPON_LEVEL : passiveMaxRankOf(id);
        const to = Math.min(cap, from + rarity.duplicate_levels);
        if (isWeapon) grantWeaponLevel(w, id, to);
        else grantPassiveRank(w, id, to);
        result = {
          kind: 'duplicate',
          id,
          rarity: rarity.id,
          display_name: displayNameOfItem(id, isWeapon),
          delta_text: isWeapon ? weaponUpgradeDelta(id, from, to) : passiveUpgradeDelta(id, from, to),
          level_from: from,
          level_to: to,
          pity_progress: st.pity,
          pity_threshold: PITY_THRESHOLD,
        };
      } else {
        const cap = isWeapon ? MAX_WEAPON_LEVEL : passiveMaxRankOf(id);
        const to = Math.min(cap, rarity.new_item_start_level);
        if (isWeapon) grantWeaponLevel(w, id, to);
        else grantPassiveRank(w, id, to);
        result = {
          kind: isWeapon ? 'weapon' : 'passive',
          id,
          rarity: rarity.id,
          display_name: displayNameOfItem(id, isWeapon),
          delta_text: isWeapon ? newWeaponDelta(id, to) : newPassiveDelta(id, to),
          level_from: 0,
          level_to: to,
          pity_progress: st.pity,
          pity_threshold: PITY_THRESHOLD,
        };
      }
    }
  }

  recomputeStats(w);
  st.readyAnnounced = -1;

  w.bus.emit('gacha_pull', {
    index: st.pulls,
    kind: result.kind,
    id: result.id,
    rarity: result.rarity,
    display_name: result.display_name,
    delta_text: result.delta_text,
    level_from: result.level_from,
    level_to: result.level_to,
    pity_progress: result.pity_progress,
    pity_threshold: result.pity_threshold,
    cost,
    balance_after: ichorBalance(w),
  });

  return result;
}

/**
 * Step 13b. One edge latch: the tick the balance first covers the next Graft, fire
 * `gacha_ready` once so the HUD can pulse. Zero allocation, no RNG, no clock.
 */
export function stepGacha(w: World): void {
  const st = stateOf(w);
  if (st.readyAnnounced === st.pulls) return;
  if (!canPull(w)) return;
  st.readyAnnounced = st.pulls;
  w.bus.emit('gacha_ready', { cost: pullCost(w), balance: ichorBalance(w), index: st.pulls + 1 });
}
